import { ConvexError } from "convex/values"

// The parts of the GitHub screens that are decisions rather than markup:
// what GitHub handed back to the callback page, that a trip is finished
// once, what each refusal is called, and what an edited mapping still owes
// the server. Kept free of React so that they can be tested as they are.

/** The `code` of an `appError()` thrown by a Convex function, when it is one. */
export function errorCode(failure: unknown): string | null {
  if (!(failure instanceof ConvexError)) return null
  const data: unknown = failure.data
  if (!data || typeof data !== "object" || !("code" in data)) return null
  return typeof data.code === "string" ? data.code : null
}

/**
 * An issue's number as people give it: `37`, `#37`, or the address of the
 * issue on GitHub. Null for anything else, a pull request's address included.
 */
export function parseIssueNumber(text: string): number | null {
  const trimmed = text.trim()
  const match = /^#?(\d{1,12})$/.exec(trimmed) ?? /\/issues\/(\d{1,12})(?:[?#].*)?$/.exec(trimmed)
  if (!match) return null
  const number = Number(match[1])
  return Number.isSafeInteger(number) && number > 0 ? number : null
}

// ---- The callback ----

export type CallbackParams = {
  state: string
  code?: string
  installationId?: number
  setupAction?: string
  error?: string
}

/**
 * What GitHub put in the address of `/github/callback`, or null when there
 * is no `state`, without which nothing can be finished.
 *
 * Read from the raw query string and not from the router's parsed search:
 * the router reads each value as JSON, and a state or a code made only of
 * digits would arrive as a number, rounded if it is long enough.
 */
export function parseCallbackSearch(search: string): CallbackParams | null {
  const query = new URLSearchParams(search)
  const state = query.get("state")
  if (!state) return null
  const text = (name: string) => query.get(name) || undefined
  const installation = query.get("installation_id")
  const installationId =
    installation && /^\d{1,15}$/.test(installation) ? Number(installation) : undefined
  return {
    state,
    code: text("code"),
    installationId: installationId !== undefined && installationId > 0 ? installationId : undefined,
    setupAction: text("setup_action"),
    error: text("error"),
  }
}

export type Completion = {
  workspaceSlug: string | null
  result: "connected" | "requested" | "error"
  reason: string | null
}

/** Every reason the interface has a sentence for. `unknown` is the rest. */
export const CALLBACK_REASONS = [
  "state_invalid",
  "state_mismatch",
  "denied",
  "code_missing",
  "exchange_failed",
  "installation_missing",
  "installation_not_yours",
  "not_org_admin",
  "installation_taken",
  "forbidden",
  "limit_reached",
  "account_taken",
  "github_unavailable",
  "not_configured",
  "unknown",
] as const
export type CallbackReason = (typeof CALLBACK_REASONS)[number]

/** The sentence to use for a reason, whatever arrived: from the server or from an address. */
export function reasonKey(reason: string | null | undefined): CallbackReason {
  return CALLBACK_REASONS.find((known) => known === reason) ?? "unknown"
}

// `complete` answers most refusals; these it throws.
const THROWN_REASONS: Record<string, CallbackReason> = {
  githubStateMismatch: "state_mismatch",
  githubNotConfigured: "not_configured",
  githubUnavailable: "github_unavailable",
}

/** A failed call to `complete`, as the outcome it amounts to. */
export function completionFromError(failure: unknown): Completion {
  const code = errorCode(failure)
  return {
    workspaceSlug: null,
    result: "error",
    reason: (code && THROWN_REASONS[code]) || "unknown",
  }
}

export type TripOutcome<C> =
  // This arrival finished the trip, and this is how it went.
  | { kind: "finished"; completion: C }
  // The state was already handed in from this browser: a refresh, or Back.
  // Nothing is sent again; `workspaceSlug` is where the first arrival led.
  | { kind: "replayed"; workspaceSlug: string | null }
export type CallbackOutcome = TripOutcome<Completion>

export type SpentStore = Pick<Storage, "getItem" | "setItem">
const SPENT_KEY = "echoes_github_trips"
const SPENT_MAX = 20

type Spent = Record<string, string | null>

function readSpent(store: SpentStore, key: string): Spent {
  try {
    const parsed: unknown = JSON.parse(store.getItem(key) ?? "{}")
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {}
    const spent: Spent = {}
    for (const [state, slug] of Object.entries(parsed)) {
      if (typeof slug === "string" || slug === null) spent[state] = slug
    }
    return spent
  } catch {
    return {}
  }
}

function writeSpent(store: SpentStore, key: string, spent: Spent) {
  try {
    const entries = Object.entries(spent).slice(-SPENT_MAX)
    store.setItem(key, JSON.stringify(Object.fromEntries(entries)))
  } catch {
    // Private mode: a refresh sends the state again, and the server, which
    // takes a state once, answers that it is no longer good.
  }
}

/**
 * Makes "finish this trip" something that happens once per state.
 *
 * A state is good for one call: the server deletes it on reading it. But the
 * page that makes the call can be run more than once with the same address:
 * React's strict mode mounts it twice, and Back or a refresh loads it again.
 * A second call would be told `state_invalid` and the person shown a failure
 * for something that worked.
 *
 * So calls under way are shared (the second mount waits for the first one's
 * answer), and a state that has been handed in is remembered for the
 * session: arriving with it again sends nothing and reports `replayed`.
 *
 * A call that throws is not remembered beyond this page: the server leaves a
 * state unspent when it refuses the caller (`githubStateMismatch`), and a
 * request that never arrived spent nothing either, so the next arrival may
 * try. This page keeps the answer it got, and does not ask again by itself.
 */
export function createCompleter(
  run: (params: CallbackParams) => Promise<Completion>,
  store: SpentStore,
) {
  return createTripCompleter(run, store, SPENT_KEY, completionFromError)
}

/**
 * `createCompleter` for any trip that comes back with a `state`: Slack's
 * too (components/slack/logic.ts). Each kind of trip remembers its states
 * under its own `key`; `fromError` is what a call that threw amounts to.
 */
export function createTripCompleter<
  P extends { state: string },
  C extends { workspaceSlug: string | null },
>(
  run: (params: P) => Promise<C>,
  store: SpentStore,
  key: string,
  fromError: (failure: unknown) => C,
) {
  const underWay = new Map<string, Promise<TripOutcome<C>>>()
  return (params: P): Promise<TripOutcome<C>> => {
    const shared = underWay.get(params.state)
    if (shared) return shared

    const spent = readSpent(store, key)
    if (params.state in spent) {
      return Promise.resolve({ kind: "replayed", workspaceSlug: spent[params.state] })
    }
    // Before the answer: a refresh while GitHub is being asked must not ask twice.
    writeSpent(store, key, { ...spent, [params.state]: null })

    const call = run(params).then(
      (completion): TripOutcome<C> => {
        writeSpent(store, key, {
          ...readSpent(store, key),
          [params.state]: completion.workspaceSlug,
        })
        return { kind: "finished", completion }
      },
      (failure: unknown): TripOutcome<C> => {
        const { [params.state]: _forgotten, ...rest } = readSpent(store, key)
        writeSpent(store, key, rest)
        return { kind: "finished", completion: fromError(failure) }
      },
    )
    underWay.set(params.state, call)
    return call
  }
}

/** What the integrations page is told in its address, after a trip. */
export type OutcomeSearch = { github?: Completion["result"]; reason?: CallbackReason }

/** Where a finished trip sends the browser, and what it says there. */
export function outcomeDestination(
  outcome: CallbackOutcome,
):
  | { to: "integrations"; workspaceSlug: string; search: OutcomeSearch }
  | { to: "root"; reason: CallbackReason | null } {
  if (outcome.kind === "replayed") {
    // The first arrival already said how it went; the page says how it is.
    return outcome.workspaceSlug === null
      ? { to: "root", reason: "state_invalid" }
      : { to: "integrations", workspaceSlug: outcome.workspaceSlug, search: {} }
  }
  const { workspaceSlug, result, reason } = outcome.completion
  if (workspaceSlug === null) {
    return { to: "root", reason: result === "error" ? reasonKey(reason) : null }
  }
  return {
    to: "integrations",
    workspaceSlug,
    search: result === "error" ? { github: result, reason: reasonKey(reason) } : { github: result },
  }
}

/** The integrations page's search, from whatever the address holds. */
export function parseOutcomeSearch(search: Record<string, unknown>): OutcomeSearch {
  const result = (["connected", "requested", "error"] as const).find(
    (known) => known === search.github,
  )
  if (!result) return {}
  if (result !== "error") return { github: result }
  return {
    github: result,
    reason: reasonKey(typeof search.reason === "string" ? search.reason : null),
  }
}

// ---- The mapping editor ----

/** One option of a GitHub Project field, and what it means here. Null is "not mapped". */
export type MappedOption<V extends string> = { id: string; name: string; value: V | null }

/**
 * What someone has changed and not saved yet, by option id. Only the
 * differences from the server are kept, so that a mapping changed elsewhere
 * meanwhile shows through wherever this person has not touched it.
 */
export type MappingEdits<V extends string> = Readonly<Record<string, V | null>>

/** The options as the editor shows them: the server's, with the edits over them. */
export function applyEdits<V extends string>(
  options: readonly MappedOption<V>[],
  edits: MappingEdits<V>,
): MappedOption<V>[] {
  return options.map((option) =>
    option.id in edits ? { ...option, value: edits[option.id] } : option,
  )
}

/** Sets one option. Choosing what the server already has withdraws the edit. */
export function editOption<V extends string>(
  options: readonly MappedOption<V>[],
  edits: MappingEdits<V>,
  optionId: string,
  value: V | null,
): MappingEdits<V> {
  const { [optionId]: _previous, ...rest } = edits
  const server = options.find((option) => option.id === optionId)
  if (!server || server.value === value) return rest
  return { ...rest, [optionId]: value }
}

/**
 * What saving would send: each edit that still differs from the server, for
 * an option the field still has. An option removed on GitHub while it was
 * being edited is dropped, which is what keeps `githubMappingInvalid` away.
 */
export function pendingChanges<V extends string>(
  options: readonly MappedOption<V>[],
  edits: MappingEdits<V>,
): { optionId: string; value: V | null }[] {
  return options
    .filter((option) => option.id in edits && edits[option.id] !== option.value)
    .map((option) => ({ optionId: option.id, value: edits[option.id] }))
}
